/* Record editing.
 *
 * ⚠ PUT /api/dns/zones/{id}/records replaces an entire RRset (name + type),
 * not a single value. Editing one value in a multi-value set means sending
 * the whole set back. A form that submits only the edited line deletes its
 * siblings, and the deletion is silent and immediate -- so every value box
 * below is a newline-joined textarea holding the WHOLE RRset, and onBlur
 * always sends the complete parsed list, never just the changed line.
 *
 * DELETE takes `name` and `record_type` (wire alias `type`) as query
 * parameters, not a body -- see backend/app/api/dns.py:751. A body on DELETE
 * is legal but not universally forwarded, and this has to survive HAProxy.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type RecordSet } from "../api";

const TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "SRV", "CAA", "NS"];

function rowKey(rrset: Pick<RecordSet, "name" | "type">) {
  return `${rrset.name}/${rrset.type}`;
}

export function RecordEditor({ zoneId }: { zoneId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ name: "", type: "A", ttl: 3600, values: "" });
  // A row-scoped message (a failed save, or a blank-out we refused to send)
  // plus a per-row remount counter. Bumping the counter for a row forces its
  // textarea -- uncontrolled, so the DOM value it shows is not React state --
  // to remount with a fresh `defaultValue`, which is what puts the server's
  // actual values back on screen after a rejected or refused edit.
  const [rowMessage, setRowMessage] = useState<{ key: string; message: string } | null>(null);
  const [resetTicks, setResetTicks] = useState<Record<string, number>>({});

  const bumpReset = (key: string) =>
    setResetTicks((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["dns", "zone", zoneId, "records"] });

  const records = useQuery({
    queryKey: ["dns", "zone", zoneId, "records"],
    queryFn: () => api.get<RecordSet[]>(`/api/dns/zones/${zoneId}/records`),
  });

  const save = useMutation({
    mutationFn: (rrset: RecordSet) =>
      api.put<RecordSet>(`/api/dns/zones/${zoneId}/records`, rrset),
    onSuccess: () => {
      setRowMessage(null);
      invalidate();
    },
    onError: (error, rrset) => {
      const key = rowKey(rrset);
      setRowMessage({ key, message: (error as Error).message });
      // The rejected edit stays in the DOM until this remount; the fresh
      // mount's `defaultValue` is read from `records.data`, which a failed
      // save never touched, so it shows what the server actually holds.
      bumpReset(key);
    },
  });

  const remove = useMutation({
    mutationFn: (rrset: Pick<RecordSet, "name" | "type">) =>
      api.del(
        `/api/dns/zones/${zoneId}/records?name=${encodeURIComponent(rrset.name)}` +
          `&type=${encodeURIComponent(rrset.type)}`,
      ),
    onSuccess: invalidate,
  });

  return (
    <div>
      <h2>Records</h2>

      {records.isLoading && <p className="muted">Loading…</p>}
      {records.isError && <p className="bad">{(records.error as Error).message}</p>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>TTL</th>
              <th>Values</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(records.data ?? []).map((rrset) => {
              const key = rowKey(rrset);
              return (
                <tr key={key}>
                  <td>{rrset.name}</td>
                  <td>{rrset.type}</td>
                  <td>{rrset.ttl}</td>
                  <td>
                    {/* One value per line: this textarea IS the whole RRset, and
                        the whole thing -- not just the edited line -- is what
                        gets sent back on blur. The `key` includes a per-row
                        reset counter so a rejected edit or a refused blank-out
                        can force a remount back to the server's values. */}
                    <textarea
                      key={`${key}/${resetTicks[key] ?? 0}`}
                      aria-label={`Values for ${rrset.name} ${rrset.type}`}
                      defaultValue={rrset.records.join("\n")}
                      rows={Math.max(1, rrset.records.length)}
                      onBlur={(event) => {
                        const values = event.target.value
                          .split("\n")
                          .map((v) => v.trim())
                          .filter(Boolean);
                        if (!values.length) {
                          // Refusing to send is correct -- an empty PUT is not
                          // how you delete an RRset, that is DELETE's job --
                          // but silence here reads as nothing having happened.
                          setRowMessage({
                            key,
                            message:
                              'A record set can’t be emptied this way. Use "Delete set" to remove it.',
                          });
                          bumpReset(key);
                          return;
                        }
                        if (values.join("\n") !== rrset.records.join("\n")) {
                          setRowMessage(null);
                          save.mutate({ ...rrset, records: values });
                        }
                      }}
                    />
                    {rowMessage?.key === key && <p className="bad">{rowMessage.message}</p>}
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete the ${rrset.type} record set for ${rrset.name}? This removes all of its values immediately and cannot be undone.`,
                          )
                        ) {
                          remove.mutate(rrset);
                        }
                      }}
                      disabled={remove.isPending}
                    >
                      Delete set
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {records.data?.length === 0 && <p className="muted">No records yet.</p>}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          const values = draft.values
            .split("\n")
            .map((v) => v.trim())
            .filter(Boolean);
          if (!draft.name.trim() || !values.length) return;
          save.mutate({
            name: draft.name.trim(),
            type: draft.type,
            ttl: draft.ttl,
            records: values,
          });
          setDraft({ name: "", type: "A", ttl: 3600, values: "" });
        }}
      >
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="www"
          aria-label="Record name"
        />
        <select
          value={draft.type}
          onChange={(e) => setDraft({ ...draft, type: e.target.value })}
          aria-label="Record type"
        >
          {TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <input
          type="number"
          value={draft.ttl}
          onChange={(e) => setDraft({ ...draft, ttl: Number(e.target.value) })}
          aria-label="TTL"
        />
        <textarea
          value={draft.values}
          onChange={(e) => setDraft({ ...draft, values: e.target.value })}
          placeholder="one value per line"
          aria-label="Record values"
        />
        <button type="submit" disabled={save.isPending}>
          Add record set
        </button>
      </form>
      {/* A failed edit to an existing row already shows its message next to
          that row; repeating it down here would just be the same sentence
          twice. This line is for what a row can't say for itself -- a
          failed *new* record set, which isn't rendered anywhere yet. */}
      {save.isError && !(records.data ?? []).some((r) => rowKey(r) === rowMessage?.key) && (
        <p className="bad">{(save.error as Error).message}</p>
      )}
    </div>
  );
}
